import { saveAs } from "file-saver";
import type { CampaignListRow } from "src/services/CampaignPerformanceAPI";
import { readFormattedText, readNumericValue } from "src/layouts/Engagements/Overview/Index";
import type { Campaign, CampaignDashboardText } from "./CampaignDashboardTypes";

/**
 * Flattens a `campaigns-list` row onto the shape this screen's columns, export
 * and filters already read. `Campaign` carries an index signature, so the extra
 * metric fields ride along and the generic `readCampaignField`/
 * `readCampaignNumber` helpers keep working unchanged.
 *
 * `sent_at` and `scheduled` are mutually exclusive — the inapplicable one is
 * the string `"-"`, which is normalised to `""` here so the existing
 * "-"-rendering in the columns stays the single place that decides how an
 * absent date looks.
 */
export const mapCampaignListRow = (row: CampaignListRow): Campaign => {
  const metrics = row.metrics;
  const sentAt = !row.sent_at || row.sent_at === "-" ? "" : row.sent_at;
  const scheduled = !row.scheduled || row.scheduled === "-" ? "" : row.scheduled;

  const campaign: Campaign = {
    campaign_id: row.campaign_id,
    campaign_name: row.campaign_name ?? "",
    draftId: row.draft_id ?? 0,
    audience: row.audience ?? "",
    created_at: sentAt,
    updated_at: sentAt,
    sent_at: sentAt,
    scheduled,
    scheduledAt: scheduled,
    source: row.source ?? "",
    sourceLabel: row.source_label ?? "",
    status: row.status ?? "",
    statusLabel: row.status_label ?? "",
    // Rates arrive as percentages already — never scale them here.
    delivered: readNumericValue(metrics?.emails_sent) ?? 0,
    deliveredFormatted: readFormattedText(metrics?.emails_sentFormattedValue) ?? readFormattedText(metrics?.emails_sent) ?? "",
    opens: readNumericValue(metrics?.opens) ?? 0,
    opensFormatted: readFormattedText(metrics?.opensFormattedValue) ?? readFormattedText(metrics?.opens) ?? "",
    clicks: 0,
    bounces: 0,
    open_rate: readNumericValue(metrics?.open_rate) ?? 0,
    open_rateFormatted: readFormattedText(metrics?.open_rateFormattedValue) ?? readFormattedText(metrics?.open_rate) ?? "",
    click_rate: readNumericValue(metrics?.click_rate) ?? 0,
    click_rateFormatted: readFormattedText(metrics?.click_rateFormattedValue) ?? readFormattedText(metrics?.click_rate) ?? "",
    bounce_rate: readNumericValue(metrics?.bounce_rate) ?? 0,
    bounce_rateFormatted: readFormattedText(metrics?.bounce_rateFormattedValue) ?? readFormattedText(metrics?.bounce_rate) ?? "",
    conversion_rate: readNumericValue(metrics?.conversion_rate) ?? 0,
    conversion_rateFormatted: readFormattedText(metrics?.conversion_rateFormattedValue) ?? readFormattedText(metrics?.conversion_rate) ?? "",
    conversions: readNumericValue(metrics?.conversions) ?? 0,
    conversionsFormatted: readFormattedText(metrics?.conversionsFormattedValue) ?? readFormattedText(metrics?.conversions) ?? "",
    // Row-level `audience` is the list name(s); this is the resolved list size.
    audience_size: readNumericValue(metrics?.audience) ?? 0,
    audience_sizeFormatted: readFormattedText(metrics?.audienceFormattedValue) ?? readFormattedText(metrics?.audience) ?? "",
    total_users: readNumericValue(metrics?.total_users) ?? 0,
    total_usersFormatted: readFormattedText(metrics?.total_usersFormattedValue) ?? readFormattedText(metrics?.total_users) ?? "",
  };

  // Keep currentStep unset when the API omits it — do not coerce to 0, or
  // canViewDraft would treat generated drafts as "not yet viewable".
  if (typeof row.current_step === "number" && Number.isFinite(row.current_step)) {
    campaign.currentStep = row.current_step;
  }

  return campaign;
};

export const getModalProps = (
  type: string,
  t: CampaignDashboardText,
) => {
  switch (type) {
    case "delete":
      return {
        title: t.deleteCampaign,
        icon: "/Icons/delete-icon.svg",
        confirmText: t.delete,
        loadingText: t.deleting,
      };
    case "archive":
      return {
        title: t.archiveCampaign,
        icon: "/Icons/archive-icon.svg",
        confirmText: t.archive,
        loadingText: t.archiving,
      };
    case "unarchive":
      return {
        title: t.unarchiveCampaign,
        icon: "/Icons/unarchive-icon.svg",
        confirmText: t.unarchive,
        loadingText: t.unarchiving,
      };
    default:
      return {
        title: t.campaignAction,
        icon: "",
        confirmText: t.confirm,
        loadingText: t.processing,
      };
  }
};

// `Campaign` carries an index signature, so the overview endpoint can return
// fields the interface doesn't declare. Read channel/status through these
// helpers rather than assuming a shape: if the backend doesn't send them the
// UI degrades to a neutral placeholder instead of inventing a value.
export const readCampaignField = (campaign: Campaign, keys: string[]): string => {
  for (const key of keys) {
    const value = campaign[key];
    if (typeof value === "string" && value.trim()) {
      return value.trim();
    }
  }
  return "";
};

export const getCampaignSource = (campaign: Campaign): string =>
  readCampaignField(campaign, ["source", "medium", "channel", "campaign_type"]);

/** Only rows backed by a wizard draft can be edited; older "draft" campaigns have no `draft_id`. */
export const getDraftId = (campaign: Campaign): number | null =>
  Number(campaign.draftId) > 0 ? Number(campaign.draftId) : null;

/** Template drafts generate their email at step 4 and AI drafts at step 8, so anything earlier has nothing to view. */
const MIN_GENERATED_DRAFT_STEP = 4;

/**
 * Whether a draft is far enough along that View should be enabled.
 * Missing/non-numeric `currentStep` is treated as unknown → allow View;
 * `handleViewCampaign` still verifies generated HTML before opening preview.
 */
export const canViewDraft = (campaign: Campaign): boolean => {
  const raw = campaign.currentStep;
  if (raw === undefined || raw === null || raw === "") {
    return true;
  }
  const step = Number(raw);
  if (!Number.isFinite(step)) {
    return true;
  }
  return step >= MIN_GENERATED_DRAFT_STEP;
};

export const getCampaignStatus =(campaign: Campaign): string =>
  readCampaignField(campaign, ["status"]);

export const getCampaignSourceLabel = (
  campaign: Campaign,
  labels: CampaignDashboardText,
  translate: (_value: string) => string,
): string => {
  const label = readCampaignField(campaign, ["sourceLabel"]) || getCampaignSource(campaign);
  return label ? translate(label) || formatSourceLabel(label, labels) : "";
};

export const formatSourceLabel = (source: string, labels: CampaignDashboardText): string => {
  const key = source.toLowerCase();
  if (key === "email") {
    return labels.email;
  }
  if (key === "whatsapp") {
    return labels.whatsapp;
  }
  return source;
};

export const formatStatusLabel = (status: string, labels: CampaignDashboardText): string => {
  const key = status.toLowerCase();
  if (key === "draft") {
    return labels.draft;
  }
  if (key === "scheduled") {
    return labels.scheduled;
  }
  if (key === "sent") {
    return labels.sent;
  }
  if (key === "active") {
    return labels.tabActive;
  }
  if (key === "archive" || key === "archived") {
    return labels.tabArchive;
  }
  return status;
};

export const matchesStatusFilter = (campaign: Campaign, statusFilter: string): boolean => {
  if (!statusFilter) {
    return true;
  }
  return getCampaignStatus(campaign).toLowerCase() === statusFilter.toLowerCase();
};

// Maps a channel value onto a known badge colour, falling back to a neutral
// variant so an unrecognised channel still renders legibly.
export const normalizeBadgeVariant = (value: string): string => {
  const normalized = value.trim().toLowerCase();
  if (normalized.includes("whatsapp")) {
    return "whatsapp";
  }
  if (normalized.includes("email") || normalized.includes("mail")) {
    return "email";
  }
  return "neutral";
};

// Distinct, non-empty values in first-seen order — drives the Source/Status
// filter options so they only ever offer values the data actually contains.
export const collectFilterOptions = (
  campaigns: Campaign[],
  read: (_campaign: Campaign) => string
): string[] => Array.from(new Set(campaigns.map(read).filter(Boolean)));

export const exportToCSV = (data: Array<Record<string, string | number>>, filename: string) => {
  if (data.length === 0) {
    return;
  }

  const headers = Object.keys(data[0]);
  const csvRows = [headers.join(",")];

  for (const row of data) {
    const values = headers.map(header => {
      const value = row[header];
      return typeof value === "string" ? `"${value.replace(/"/g, "\"\"")}"` : value;
    });
    csvRows.push(values.join(","));
  }

  const blob = new Blob([csvRows.join("\n")], { type: "text/csv;charset=utf-8;" });
  saveAs(blob, `${filename}.csv`);
};
